import { TaskCategory } from '@/types/task';

export interface CategoryConfig {
  displayName: string;
  icon: string; // Lucide icon name (PascalCase component name)
  color: string; // Tailwind classes for category badge
  predefinedActions: string[];
}

export const CATEGORY_CONFIG: Record<TaskCategory, CategoryConfig> = {
  [TaskCategory.FoodSource]: {
    displayName: 'Food Source',
    icon: 'Wheat',
    color: 'bg-[#1a3325] text-[#4a8c5c]',
    predefinedActions: ['till', 'plant', 'fertilize', 'weed_control', 'soil_test', 'mow', 'frost_seed', 'spray', 'other'],
  },
  [TaskCategory.StandPrep]: {
    displayName: 'Stand Prep',
    icon: 'TreePine',
    color: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
    predefinedActions: ['hang', 'inspect', 'trim_lanes', 'install_lifeline', 'remove', 'relocate', 'other'],
  },
  [TaskCategory.TrailCameras]: {
    displayName: 'Trail Cameras',
    icon: 'Camera',
    color: 'bg-[#182028] text-[#5a7a8a]',
    predefinedActions: ['deploy', 'pull_card', 'swap_battery', 'reposition', 'analyze', 'transition', 'other'],
  },
  [TaskCategory.Scouting]: {
    displayName: 'Scouting',
    icon: 'Search',
    color: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
    predefinedActions: ['post_season', 'shed_hunt', 'spring_scout', 'summer_scout', 'pre_season', 'in_season', 'other'],
  },
  [TaskCategory.GearEquipment]: {
    displayName: 'Gear & Equipment',
    icon: 'Wrench',
    color: 'bg-[var(--color-elevated)] text-[var(--color-text-primary)]',
    predefinedActions: ['tune', 'zero', 'sharpen', 'clean', 'pack', 'store', 'other'],
  },
  [TaskCategory.PropertyInfrastructure]: {
    displayName: 'Property Infrastructure',
    icon: 'Hammer',
    color: 'bg-[var(--color-elevated)] text-[var(--color-text-secondary)]',
    predefinedActions: ['clear', 'repair', 'build', 'maintain', 'other'],
  },
  [TaskCategory.HabitatManagement]: {
    displayName: 'Habitat Management',
    icon: 'Trees',
    color: 'bg-[#1a3325] text-[#4a8c5c]',
    predefinedActions: ['cut', 'girdle', 'burn', 'thin', 'plant', 'other'],
  },
  [TaskCategory.RegulatoryAdmin]: {
    displayName: 'Regulatory / Admin',
    icon: 'FileText',
    color: 'bg-[var(--color-elevated)] text-[var(--color-text-secondary)]',
    predefinedActions: ['purchase', 'apply', 'renew', 'file', 'other'],
  },
  [TaskCategory.PostSeason]: {
    displayName: 'Post-Season',
    icon: 'ClipboardCheck',
    color: 'bg-[#182028] text-[#5a7a8a]',
    predefinedActions: ['evaluate', 'document', 'review', 'plan', 'other'],
  },
  [TaskCategory.PredatorControl]: {
    displayName: 'Predator Control',
    icon: 'Shield',
    color: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
    predefinedActions: ['trap', 'hunt', 'monitor', 'other'],
  },
  [TaskCategory.WaterholeManagement]: {
    displayName: 'Waterhole Management',
    icon: 'Droplets',
    color: 'bg-[#182028] text-[#5a7a8a]',
    predefinedActions: ['create', 'maintain', 'check', 'other'],
  },
  [TaskCategory.WildlifeStructures]: {
    displayName: 'Wildlife Structures',
    icon: 'Bird',
    color: 'bg-[#1a3325] text-[#4a8c5c]',
    predefinedActions: ['build', 'install', 'maintain', 'check', 'other'],
  },
  [TaskCategory.MineralNutrition]: {
    displayName: 'Mineral & Nutrition',
    icon: 'Gem',
    color: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]',
    predefinedActions: ['deploy', 'refresh', 'relocate', 'other'],
  },
  [TaskCategory.NeighborAccess]: {
    displayName: 'Neighbor / Access',
    icon: 'Users',
    color: 'bg-[var(--color-elevated)] text-[var(--color-text-primary)]',
    predefinedActions: ['contact', 'meet', 'negotiate', 'document', 'other'],
  },
  [TaskCategory.HerdData]: {
    displayName: 'Herd Data',
    icon: 'BarChart2',
    color: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]',
    predefinedActions: ['survey', 'count', 'analyze', 'document', 'other'],
  },
  [TaskCategory.MockScrapes]: {
    displayName: 'Mock Scrapes',
    icon: 'Target',
    color: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
    predefinedActions: ['create', 'refresh', 'monitor', 'other'],
  },
  [TaskCategory.TreePlanting]: {
    displayName: 'Tree Planting',
    icon: 'TreeDeciduous',
    color: 'bg-[#1a3325] text-[#4a8c5c]',
    predefinedActions: ['order', 'plant', 'prune', 'protect', 'other'],
  },
  [TaskCategory.Other]: {
    displayName: 'Other',
    icon: 'MoreHorizontal',
    color: 'bg-[var(--color-elevated)] text-[var(--color-text-muted)]',
    predefinedActions: [],
  },
};

export function getCategoryDisplayName(category: TaskCategory): string {
  return CATEGORY_CONFIG[category].displayName;
}

export function getCategoryIcon(category: TaskCategory): string {
  return CATEGORY_CONFIG[category].icon;
}

export function getCategoryActions(category: TaskCategory): string[] {
  return CATEGORY_CONFIG[category].predefinedActions;
}

/**
 * Converts snake_case action keys to Title Case display names.
 * e.g. "weed_control" → "Weed Control"
 */
export function getActionDisplayName(action: string): string {
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
